import { useMemo } from 'react';

import {
  Commission,
  Referral,
  ReferralCommissionEntry,
  ReferralProfile,
  ReferralReferredUser,
  ReferralSummary,
} from '../../lib/models/crm/types';

import { toCommissionRows, toReferralRows } from './helpers';

type Params = {
  referralCommissions: ReferralCommissionEntry[];
  referralProfile: ReferralProfile | null;
  referredUsers: ReferralReferredUser[];
  visibleCount: number;
};

type ReferralsViewData = {
  commissions: Commission[];
  hasMoreCommissions: boolean;
  hasMoreReferrals: boolean;
  referrals: Referral[];
  summary: ReferralSummary;
};

const useReferrals = ({
  referralCommissions,
  referralProfile,
  referredUsers,
  visibleCount,
}: Params): ReferralsViewData => {
  const summary = useMemo<ReferralSummary>(() => ({
    code: referralProfile?.code ?? '',
    link: referralProfile?.referral_link ?? '',
    registrations: referralProfile?.referred_registered_count ?? 0,
    purchases: referralProfile?.referred_purchased_count ?? 0,
  }), [referralProfile]);

  const referrals = useMemo(
    () => toReferralRows(referredUsers).slice(0, visibleCount),
    [referredUsers, visibleCount],
  );

  const commissions = useMemo(
    () => toCommissionRows(referralCommissions).slice(0, visibleCount),
    [referralCommissions, visibleCount],
  );

  const hasMoreReferrals = referredUsers.length > visibleCount;
  const hasMoreCommissions = referralCommissions.length > visibleCount;

  return { commissions, hasMoreCommissions, hasMoreReferrals, referrals, summary };
};

export default useReferrals;
